import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Users, BookOpen, Target, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import QuizPreview from '@/components/QuizPreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Home() {
  const [teachers, setTeachers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetchTeachers();
    fetchTestimonials();
  }, []);

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${API}/teachers`);
      setTeachers(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials?status=approved`);
      setTestimonials(response.data); // Show ALL approved testimonials
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  return (
    <div>
      <section className="relative bg-gradient-to-br from-blue-50 to-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div data-testid="hero-content">
              <h1 className="text-4xl md:text-6xl font-bold text-brand-dark mb-6 tracking-tight leading-tight">
                Master in Engineering, JEE & NEET Concepts
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
                Deep conceptual clarity + exam-oriented preparation through personalized 1:1 and group tutoring for Engineering, JEE, and NEET aspirants
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/enroll">
                  <Button data-testid="hero-enroll-btn" className="btn-primary">
                    Enroll Now <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/courses">
                  <Button data-testid="hero-courses-btn" className="btn-secondary">
                    View Courses
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1741699428553-41c8e5bd894d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHx1bml2ZXJzaXR5JTIwc3R1ZGVudCUyMHN0dWR5aW5nJTIwbGlicmFyeXxlbnwwfHx8fDE3NzQwMjQ1Nzl8MA&ixlib=rb-4.1.0&q=85"
                alt="Engineering student focused on study"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-brand-dark mb-4">Our Services</h2>
            <p className="text-lg text-slate-600">Choose the learning mode that suits you best</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div data-testid="service-card-1on1" className="feature-card group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-blue/5 rounded-bl-full transition-all group-hover:w-40 group-hover:h-40"></div>
              <Users className="h-12 w-12 text-brand-blue mb-4" />
              <h3 className="text-2xl font-semibold text-brand-dark mb-3 font-display">1:1 Personalized Tutoring</h3>
              <p className="text-slate-600 mb-4">
                Get individual attention from expert tutors. Customized learning pace and focused doubt solving.
              </p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0" />
                  <span>Personalized learning plan</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0" />
                  <span>Flexible scheduling</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0" />
                  <span>Direct interaction with teacher</span>
                </li>
              </ul>
            </div>

            <div data-testid="service-card-group" className="feature-card group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-blue/5 rounded-bl-full transition-all group-hover:w-40 group-hover:h-40"></div>
              <BookOpen className="h-12 w-12 text-brand-blue mb-4" />
              <h3 className="text-2xl font-semibold text-brand-dark mb-3 font-display">Group Classes</h3>
              <p className="text-slate-600 mb-4">
                Learn collaboratively with peers. Interactive sessions with competitive environment.
              </p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0" />
                  <span>Cost-effective learning</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0" />
                  <span>Peer learning environment</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0" />
                  <span>Regular assessments</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-surface">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-brand-dark mb-4">Why Choose Tutor Flex?</h2>
            <p className="text-lg text-slate-600">Your success is our mission</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div data-testid="why-card-1" className="text-center">
              <div className="w-20 h-20 bg-brand-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="h-10 w-10 text-brand-blue" />
              </div>
              <h3 className="text-xl font-semibold text-brand-dark mb-2 font-display">Concept Clarity</h3>
              <p className="text-slate-600">
                Deep understanding of fundamentals with practical applications and real-world examples.
              </p>
            </div>

            <div data-testid="why-card-2" className="text-center">
              <div className="w-20 h-20 bg-brand-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-10 w-10 text-brand-blue" />
              </div>
              <h3 className="text-xl font-semibold text-brand-dark mb-2 font-display">Expert Faculty</h3>
              <p className="text-slate-600">
                Learn from experienced educators with proven track records in GATE and engineering education.
              </p>
            </div>

            <div data-testid="why-card-3" className="text-center">
              <div className="w-20 h-20 bg-brand-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-10 w-10 text-brand-blue" />
              </div>
              <h3 className="text-xl font-semibold text-brand-dark mb-2 font-display">Results-Driven</h3>
              <p className="text-slate-600">
                Proven methodology focused on both academic excellence and competitive exam success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Quiz Preview */}
      <QuizPreview />

      {teachers.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-brand-dark mb-4">Meet Our Expert Teachers</h2>
              <p className="text-lg text-slate-600">Learn from the best in the field</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {teachers.map((teacher) => (
                <div key={teacher.teacher_id} data-testid={`teacher-card-${teacher.teacher_id}`} className="feature-card text-center">
                  <img
                    src={teacher.photo_url}
                    alt={teacher.name}
                    className="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4 border-brand-blue/10"
                  />
                  <h3 className="text-xl font-semibold text-brand-dark mb-2 font-display">{teacher.name}</h3>
                  <p className="text-brand-blue font-medium mb-2">{teacher.subject}</p>
                  <p className="text-sm text-slate-600 mb-3">{teacher.experience}</p>
                  <p className="text-slate-600 text-sm">{teacher.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link to="/teachers">
                <Button data-testid="view-all-teachers-btn" className="btn-secondary">
                  View All Teachers <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {testimonials.length > 0 && (
        <section className="py-20 bg-brand-surface">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-brand-dark mb-4">Student Testimonials</h2>
              <p className="text-lg text-slate-600">Hear from our successful students</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.testimonial_id}
                  data-testid={`testimonial-card-${testimonial.testimonial_id}`}
                  className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all"
                >
                  <div className="flex items-center mb-4">
                    {testimonial.student_photo && (
                      <img
                        src={testimonial.student_photo}
                        alt={testimonial.student_name}
                        className="w-12 h-12 rounded-full object-cover mr-3"
                      />
                    )}
                    <div>
                      <h4 className="font-semibold text-brand-dark">{testimonial.student_name}</h4>
                      <p className="text-sm text-slate-500">{testimonial.course_taken}</p>
                    </div>
                  </div>
                  <p className="text-slate-600 italic">"{testimonial.feedback}"</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 bg-brand-dark text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Ready to Master Engineering, JEE & NEET?</h2>
          <p className="text-lg text-slate-300 mb-8">
            Join hundreds of students who have achieved their academic goals with Tutor Flex
          </p>
          <Link to="/enroll">
            <Button data-testid="cta-enroll-btn" size="lg" className="bg-brand-blue hover:bg-blue-600 text-white rounded-full px-10 py-4 text-lg">
              Enroll Now <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
